import type { ClientListItem } from "@planerius/contracts/cabinet"
import { useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useEffect, useMemo, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../access/use-access"
import { fetchClients } from "../../api/clients.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"
import { todayInZone } from "../../lib/format"
import { DeniedScreen } from "../../shell/states"
import { EmptyState } from "../../ui/empty-state"
import { SkeletonText } from "../../ui/skeleton"
import { buildClientGroups } from "./master-clients"
import styles from "./master-clients-screen.module.css"
import { MasterFrame, MasterHead } from "./master-frame"

/**
 * 5-М4 «Мои клиенты».
 *
 * ЭТО НЕ БАЗА КЛИЕНТОВ САЛОНА И НЕ ЕЁ УРЕЗАННАЯ КОПИЯ. Сервер сужает выдачу до тех,
 * у кого есть хоть одна запись к этому мастеру (признак роли restrict_to_own_records).
 * Поэтому здесь нет ни фильтров по меткам, ни выгрузки, ни «показать всех» — не потому,
 * что их спрятали, а потому, что за ними ничего нет.
 *
 * ПОЧЕМУ ЭКРАН МОЖЕТ ОТКАЗАТЬ. GET /v1/cabinet/clients требует права clients.list.view,
 * а у роли «мастер» оно по умолчанию выключено (ADR-024). То есть салон, не выдавший
 * это право, закрывает мастеру и «Моих клиентов» — целиком, вместе с его собственными
 * посетителями. Отдельного маршрута «только мои клиенты» в API нет; пробел вынесен
 * в отчёт. Пока его нет, экран честно показывает отказ по праву, а не пустой список:
 * пустой список означал бы «вы никого не обслуживали», то есть враньё.
 */

export interface MasterClientsSearch {
  readonly q?: string | undefined
}

interface ListState {
  readonly items: readonly ClientListItem[]
  readonly loading: boolean
  readonly problem: ApiProblem | null
}

export function MasterClientsScreen(props: { readonly search: MasterClientsSearch }): ReactNode {
  const { context } = useCabinetAccess()
  const canList = useHasPermission("clients.list.view")
  const navigate = useNavigate()

  const query = props.search.q ?? ""
  const [input, setInput] = useState(query)
  const [state, setState] = useState<ListState>({ items: [], loading: canList, problem: null })

  const timeZone = context.branches[0]?.timezone ?? context.tenant.defaultTimezone
  const employeeId = context.employeeId
  const restrictsItself = context.role.restrictToOwnRecords

  // Адрес — источник правды для запроса, поле ввода — для набора. Иначе ссылка
  // на найденное («посмотри, вот он») открывала бы пустой список.
  useEffect(() => {
    setInput(query)
  }, [query])

  useEffect(() => {
    if (!canList) return undefined
    const controller = new AbortController()
    setState((previous) => ({ ...previous, loading: true, problem: null }))

    const run = async (): Promise<void> => {
      try {
        const result = await fetchClients(
          {
            ...(query.trim() === "" ? {} : { search: query.trim() }),
            // Сужение до своих делает СЕРВЕР по признаку роли. Если у роли его нет
            // (мастер, который одновременно администратор), сузить обязан запрос —
            // иначе экран с заголовком «Мои клиенты» показал бы базу салона.
            ...(restrictsItself || employeeId === null ? {} : { lastVisitEmployeeId: employeeId }),
            sort: "last_visit",
            limit: 50,
          },
          controller.signal,
        )
        if (controller.signal.aborted) return
        setState({ items: result.items, loading: false, problem: null })
      } catch (error) {
        if (controller.signal.aborted) return
        setState({
          items: [],
          loading: false,
          problem: isApiProblem(error) ? error : transportProblem("unknown"),
        })
      }
    }
    void run()

    return () => {
      controller.abort()
    }
  }, [canList, query, restrictsItself, employeeId])

  const groups = useMemo(
    () =>
      buildClientGroups({
        items: state.items,
        timeZone,
        today: todayInZone(timeZone),
      }),
    [state.items, timeZone],
  )

  if (!canList) {
    return (
      <MasterFrame active="clients">
        <DeniedScreen sectionLabel="Мои клиенты" required="clients.list.view" />
      </MasterFrame>
    )
  }

  const total = state.items.length
  const searching = query.trim() !== ""

  return (
    <MasterFrame active="clients">
      <MasterHead
        title="Мои клиенты"
        subtitle={state.loading ? "Загружаем…" : `Те, кого вы обслуживали · ${String(total)}`}
      />

      <div className={styles.searchRow}>
        <input
          className={styles.search}
          type="search"
          value={input}
          placeholder="Имя клиента"
          aria-label="Поиск по имени клиента"
          onChange={(event) => {
            setInput(event.currentTarget.value)
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return
            void navigate({
              to: "/my/clients",
              search: { q: input.trim() === "" ? undefined : input.trim() },
            })
          }}
        />
      </div>

      <div className={styles.list}>
        {state.loading ? (
          <div className={styles.skeleton}>
            <SkeletonText widths={[180, 120]} height={13} />
            <SkeletonText widths={[160, 140]} height={13} />
            <SkeletonText widths={[200, 110]} height={13} />
          </div>
        ) : null}

        {!state.loading && state.problem !== null ? (
          <EmptyState
            title="Список не загрузился"
            text="Проверьте связь и повторите — ваши клиенты появятся здесь. Полную базу салона ведёт администратор."
          />
        ) : null}

        {!state.loading && state.problem === null && total === 0 ? (
          searching ? (
            <EmptyState
              title="Среди ваших клиентов таких нет"
              text="Полную базу салона ведёт администратор. Записать нового клиента можно по точному номеру из «Моего дня»."
            />
          ) : (
            <EmptyState
              title="Здесь появятся клиенты, которых вы обслужите"
              text="Список собирается из ваших записей сам."
            />
          )
        ) : null}

        {groups.map((group) => (
          <section key={group.title} className={styles.group}>
            <h2 className={styles.groupTitle}>{group.title}</h2>
            {group.items.map((row) => (
              <div key={row.id} className={styles.row}>
                <span className={styles.avatar} aria-hidden="true">
                  {row.initials}
                </span>
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{row.displayName}</span>
                  <span className={styles.rowSub}>{row.subtitle}</span>
                </span>
                {/* Метка «неявка» — слово, а не цветная точка: цвет в одиночку смысла
                    не несёт, а решение «звонить или нет» человек принимает по слову. */}
                {row.hasNoShow ? <span className={styles.noShow}>неявка</span> : null}
                {/* Кнопки звонка нет ВОВСЕ, когда телефон мастеру не открыт: это
                    отсутствие блока, а не серая кнопка (§5.2). */}
                {row.phone === null ? null : (
                  <a
                    className={styles.call}
                    href={`tel:${row.phone}`}
                    aria-label={`Позвонить: ${row.displayName}`}
                  >
                    <span aria-hidden="true">☏</span>
                  </a>
                )}
              </div>
            ))}
          </section>
        ))}

        {total > 0 ? (
          <p className={styles.footNote}>
            Здесь только те, кого вы обслуживали. Полную базу салона ведёт администратор.
          </p>
        ) : null}
      </div>
    </MasterFrame>
  )
}
